/**
 * Counter feedback beeps for scanning, synthesised with the Web Audio API — no sound files to ship or load.
 *
 *  - success: "card payment approved" — one clear beep, like a card machine accepting a payment
 *  - fail:    "self-checkout error" — a two-note drop, clearly wrong without sounding like an alarm
 *
 * Browsers only allow audio after a user gesture, so call `unlockScanSounds()` from a click / key handler
 * (Open camera, Scan, Enter); sounds played later — e.g. from automatic camera detection — then work.
 */

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const Ctor =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

/** Create / resume the audio context. Call from a user gesture. */
export function unlockScanSounds(): void {
  const a = audio();
  if (a && a.state === "suspended") void a.resume().catch(() => {});
}

/**
 * One tone: frequency (Hz), start offset and length (s), waveform and peak volume. A 5 ms attack, held at
 * full volume, then a 20 ms release — a clean "beep" without clicks (same shape as the samples chosen).
 */
function tone(a: AudioContext, freq: number, at: number, length: number, type: OscillatorType, volume: number) {
  const osc = a.createOscillator();
  const gain = a.createGain();
  const t = a.currentTime + at;
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(volume, t + 0.005);
  gain.gain.setValueAtTime(volume, t + Math.max(0.006, length - 0.02));
  gain.gain.exponentialRampToValueAtTime(0.0001, t + length);
  osc.connect(gain).connect(a.destination);
  osc.start(t);
  osc.stop(t + length + 0.03);
}

export function playScanSound(kind: "success" | "fail"): void {
  const a = audio();
  if (!a) return;
  if (a.state === "suspended") void a.resume().catch(() => {});
  try {
    if (kind === "success") {
      // "Card payment approved": one clear 1.9 kHz beep.
      tone(a, 1900, 0, 0.22, "sine", 0.35);
    } else {
      // "Self-checkout error": G5 dropping to C5.
      tone(a, 784, 0, 0.22, "sine", 0.35);
      tone(a, 523, 0.26, 0.4, "sine", 0.35);
    }
  } catch {
    // Audio is a nicety — never let it break scanning.
  }
}
